import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X, ChevronLeft, ChevronRight } from "lucide-react";
import { useLang } from "@/i18n";
import { GALLERY } from "@/photos";
import { FadeUp, EASE } from "./Reveal";

export default function Gallery() {
  const { lang, t } = useLang();
  const [active, setActive] = useState(null);

  useEffect(() => {
    if (active === null) return;
    const onKey = (e) => {
      if (e.key === "Escape") setActive(null);
      if (e.key === "ArrowRight") setActive((a) => (a + 1) % GALLERY.length);
      if (e.key === "ArrowLeft") setActive((a) => (a - 1 + GALLERY.length) % GALLERY.length);
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [active === null]);

  return (
    <section id="casa" className="bg-white py-24 sm:py-32">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <FadeUp>
          <p className="text-xs font-bold uppercase tracking-[0.3em] text-pink-600">{t.gallery.eyebrow}</p>
          <h2 className="font-display mt-3 max-w-2xl text-3xl font-bold tracking-tight text-stone-900 sm:text-4xl lg:text-5xl">
            {t.gallery.title}
          </h2>
          <p className="mt-4 max-w-xl text-base leading-relaxed text-stone-500 sm:text-lg">{t.gallery.subtitle}</p>
        </FadeUp>

        <div className="mt-14 columns-1 gap-5 sm:columns-2 lg:columns-3" data-testid="gallery-grid">
          {GALLERY.map((photo, i) => (
            <FadeUp key={i} delay={(i % 3) * 0.1} className="mb-5 break-inside-avoid">
              <button
                type="button"
                onClick={() => setActive(i)}
                data-testid={`gallery-image-${i}`}
                className="frame-clip group relative block w-full cursor-zoom-in overflow-hidden bg-stone-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-pink-500"
                aria-label={photo.alt[lang]}
              >
                <img
                  src={photo.src}
                  alt={photo.alt[lang]}
                  loading="lazy"
                  className="w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                />
                <span className="pointer-events-none absolute inset-x-0 bottom-0 translate-y-3 bg-gradient-to-t from-stone-950/70 to-transparent p-5 text-left text-sm font-medium text-white opacity-0 transition-[opacity,transform] duration-500 group-hover:translate-y-0 group-hover:opacity-100">
                  {photo.alt[lang]}
                </span>
              </button>
            </FadeUp>
          ))}
        </div>
      </div>

      <AnimatePresence>
        {active !== null && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-[80] flex items-center justify-center bg-stone-950/92 p-4 backdrop-blur-md sm:p-10"
            onClick={() => setActive(null)}
            data-testid="lightbox"
          >
            <button
              type="button"
              onClick={() => setActive(null)}
              data-testid="lightbox-close"
              aria-label="Close"
              className="absolute right-5 top-5 z-10 flex h-11 w-11 items-center justify-center rounded-full bg-white/10 text-white backdrop-blur-md transition-colors duration-300 hover:bg-pink-600"
            >
              <X className="h-5 w-5" />
            </button>

            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setActive((a) => (a - 1 + GALLERY.length) % GALLERY.length);
              }}
              data-testid="lightbox-prev"
              aria-label="Previous"
              className="absolute left-3 top-1/2 z-10 flex h-12 w-12 -translate-y-1/2 items-center justify-center rounded-full bg-white/10 text-white backdrop-blur-md transition-colors duration-300 hover:bg-pink-600 sm:left-6"
            >
              <ChevronLeft className="h-6 w-6" />
            </button>

            <motion.figure
              key={active}
              initial={{ opacity: 0, scale: 0.94, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.97 }}
              transition={{ duration: 0.45, ease: EASE }}
              className="relative max-h-full"
              onClick={(e) => e.stopPropagation()}
            >
              <img
                src={GALLERY[active].src}
                alt={GALLERY[active].alt[lang]}
                className="max-h-[78vh] w-auto max-w-full rounded-2xl object-contain shadow-2xl"
              />
              <figcaption className="mt-4 flex items-center justify-between gap-4 text-sm text-white/80">
                <span className="font-medium">{GALLERY[active].alt[lang]}</span>
                <span className="font-mono text-xs tracking-widest text-pink-300" data-testid="lightbox-counter">
                  {active + 1} / {GALLERY.length}
                </span>
              </figcaption>
            </motion.figure>

            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setActive((a) => (a + 1) % GALLERY.length);
              }}
              data-testid="lightbox-next"
              aria-label="Next"
              className="absolute right-3 top-1/2 z-10 flex h-12 w-12 -translate-y-1/2 items-center justify-center rounded-full bg-white/10 text-white backdrop-blur-md transition-colors duration-300 hover:bg-pink-600 sm:right-6"
            >
              <ChevronRight className="h-6 w-6" />
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
